<script setup lang="ts">
import { ArrowLeft, Check, Home, MicVocal, Play, RotateCcw, Send, Square, Volume2 } from 'lucide-vue-next';
import { computed, onBeforeUnmount, ref } from 'vue';
import { useFamily } from '@/composables/useFamily';
import { useNavigation } from '@/composables/useNavigation';

type VoiceState = 'idle' | 'recording' | 'ready' | 'sent';
const MAX_AUDIO_SECONDS = 20;

const { addPendingVoiceMessage, selectedFriend } = useFamily();
const { navigate } = useNavigation();

const recorder = ref<MediaRecorder | null>(null);
const stream = ref<MediaStream | null>(null);
const audioPreview = ref<HTMLAudioElement | null>(null);
const audioUrl = ref('');
const audioBlob = ref<Blob | null>(null);
const voiceState = ref<VoiceState>('idle');
const errorText = ref('');
const seconds = ref(0);

let chunks: BlobPart[] = [];
let timerId = 0;

const friendName = computed(() => selectedFriend.value?.displayName ?? 'Freund');
const canSend = computed(() => voiceState.value === 'ready' && audioUrl.value);

onBeforeUnmount(() => {
  cleanupRecording();
  revokeAudioUrl();
});

async function startRecording() {
  if (voiceState.value === 'recording') return;

  errorText.value = '';
  revokeAudioUrl();
  chunks = [];
  seconds.value = 0;

  try {
    const nextStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const nextRecorder = new MediaRecorder(nextStream);
    stream.value = nextStream;
    recorder.value = nextRecorder;

    nextRecorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    });

    nextRecorder.addEventListener('stop', () => {
      const blob = new Blob(chunks, { type: nextRecorder.mimeType || 'audio/webm' });
      audioBlob.value = blob;
      audioUrl.value = URL.createObjectURL(blob);
      voiceState.value = 'ready';
      stopTimer();
      stopStream();
      recorder.value = null;
    });

    nextRecorder.start();
    voiceState.value = 'recording';
    startTimer();
  } catch {
    errorText.value = 'Mikrofon konnte nicht gestartet werden.';
    voiceState.value = 'idle';
    cleanupRecording();
  }
}

function stopRecording() {
  if (!recorder.value || recorder.value.state === 'inactive') return;
  recorder.value.stop();
}

function resetRecording() {
  cleanupRecording();
  revokeAudioUrl();
  errorText.value = '';
  voiceState.value = 'idle';
  seconds.value = 0;
}

async function sendVoiceMessage() {
  if (!canSend.value || !selectedFriend.value || !audioBlob.value) return;

  await addPendingVoiceMessage(selectedFriend.value.friendChildId, audioBlob.value);
  voiceState.value = 'sent';
}

async function playPreview() {
  await audioPreview.value?.play().catch(() => undefined);
}

function returnToFriend() {
  navigate('friend');
}

function startTimer() {
  stopTimer();
  timerId = window.setInterval(() => {
    seconds.value += 1;
    if (seconds.value >= MAX_AUDIO_SECONDS) {
      stopRecording();
    }
  }, 1000);
}

function stopTimer() {
  if (!timerId) return;
  window.clearInterval(timerId);
  timerId = 0;
}

function cleanupRecording() {
  if (recorder.value && recorder.value.state !== 'inactive') {
    recorder.value.stop();
  }
  stopTimer();
  stopStream();
  recorder.value = null;
}

function stopStream() {
  if (!stream.value) return;
  stream.value.getTracks().forEach((track) => track.stop());
  stream.value = null;
}

function revokeAudioUrl() {
  if (!audioUrl.value) return;
  URL.revokeObjectURL(audioUrl.value);
  audioUrl.value = '';
  audioBlob.value = null;
}
</script>

<template>
  <section class="mx-auto max-w-5xl px-4 py-6 pb-28 sm:px-6 md:pb-10">
    <button class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-full bg-white px-5 font-black text-heart-ink shadow-sm" type="button" @click="navigate('friend')">
      <ArrowLeft class="h-5 w-5" aria-hidden="true" />
      Zurueck
    </button>

    <div class="mt-6 rounded-[34px] bg-white p-5 text-center shadow-soft md:p-8">
      <div class="mx-auto flex h-20 w-20 items-center justify-center rounded-[26px] bg-sky-100 text-sky-800">
        <MicVocal class="h-12 w-12" aria-hidden="true" />
      </div>
      <h1 class="mt-4 text-4xl font-black text-heart-berry md:text-6xl">Stimme fuer {{ friendName }}</h1>

      <div class="voice-stage mx-auto mt-6 max-w-2xl">
        <div class="voice-house" aria-hidden="true">
          <Home class="h-12 w-12 fill-current" />
          <span>{{ friendName.slice(0, 1) }}</span>
        </div>

        <div v-if="voiceState === 'recording'" class="voice-wave voice-wave-a" aria-hidden="true" />
        <div v-if="voiceState === 'recording'" class="voice-wave voice-wave-b" aria-hidden="true" />

        <button
          v-if="voiceState !== 'recording'"
          class="focus-ring voice-record-button mx-auto"
          type="button"
          aria-label="Sprachnachricht aufnehmen"
          :disabled="voiceState === 'sent'"
          @click="startRecording"
        >
          <MicVocal class="h-24 w-24 md:h-32 md:w-32" aria-hidden="true" />
        </button>

        <button
          v-else
          class="focus-ring voice-stop-button mx-auto"
          type="button"
          aria-label="Aufnahme stoppen"
          @click="stopRecording"
        >
          <Square class="h-24 w-24 fill-white md:h-32 md:w-32" aria-hidden="true" />
        </button>
      </div>

      <div class="mt-6 flex items-center justify-center gap-3">
        <span
          class="grid h-14 min-w-20 place-items-center rounded-full px-5 text-2xl font-black"
          :class="voiceState === 'recording' ? 'bg-rose-100 text-rose-800' : 'bg-heart-cream text-heart-ink'"
          aria-label="Aufnahmedauer"
        >
          {{ seconds }}s
        </span>
        <span v-if="voiceState === 'recording'" class="inline-flex h-14 items-center gap-2 rounded-full bg-rose-100 px-5 text-rose-800" aria-hidden="true">
          <span class="h-4 w-4 rounded-full bg-rose-600 voice-record-dot" />
          <Volume2 class="h-7 w-7" />
        </span>
      </div>

      <div v-if="voiceState === 'ready'" class="mt-6 rounded-[26px] bg-heart-cream p-4">
        <audio ref="audioPreview" class="w-full" controls :src="audioUrl" aria-label="Sprachnachricht anhoeren" />
        <div class="mt-4 grid gap-3 sm:grid-cols-3">
          <button class="focus-ring flex min-h-20 items-center justify-center rounded-[24px] bg-white text-heart-ink shadow-sm" type="button" aria-label="Anhoeren" @click="playPreview">
            <Play class="h-10 w-10 fill-current" aria-hidden="true" />
          </button>
          <button class="focus-ring flex min-h-20 items-center justify-center rounded-[24px] bg-slate-100 text-heart-ink shadow-sm" type="button" aria-label="Neu aufnehmen" @click="resetRecording">
            <RotateCcw class="h-10 w-10" aria-hidden="true" />
          </button>
          <button class="focus-ring flex min-h-20 items-center justify-center rounded-[24px] bg-heart-leaf text-white shadow-sm" type="button" aria-label="Sprachnachricht senden" @click="sendVoiceMessage">
            <Send class="h-11 w-11" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div v-if="voiceState === 'sent'" class="mt-6 rounded-[28px] bg-heart-mint p-5">
        <Check class="mx-auto h-16 w-16 text-heart-leaf" aria-hidden="true" />
        <p class="mt-3 text-2xl font-black text-heart-leaf">Bei Mama oder Papa</p>
        <button class="focus-ring mt-5 min-h-16 w-full rounded-[24px] bg-heart-leaf px-5 text-xl font-black text-white" type="button" @click="returnToFriend">
          Zurueck zu {{ friendName }}
        </button>
      </div>

      <p v-if="errorText" class="mt-5 rounded-2xl bg-rose-100 px-4 py-3 text-xl font-black text-rose-800">
        {{ errorText }}
      </p>
    </div>
  </section>
</template>

<style scoped>
.voice-stage {
  position: relative;
  min-height: 19rem;
  overflow: hidden;
  border-radius: 2rem;
  background:
    radial-gradient(circle at 18% 22%, rgba(255, 255, 255, 0.8), transparent 19%),
    linear-gradient(180deg, #e0f2fe 0%, #fff7ed 68%, #dcfce7 100%);
}

.voice-record-button,
.voice-stop-button {
  position: absolute;
  left: 50%;
  top: 50%;
  display: grid;
  width: min(15rem, 58vw);
  height: min(15rem, 58vw);
  place-items: center;
  border-radius: 999px;
  transform: translate(-50%, -50%);
  transition:
    transform 180ms ease,
    box-shadow 180ms ease;
}

.voice-record-button {
  background: #0ea5e9;
  color: #ffffff;
  box-shadow:
    0 22px 46px rgba(14, 165, 233, 0.25),
    inset 0 -0.8rem 0 rgba(3, 105, 161, 0.22);
}

.voice-record-button:active,
.voice-stop-button:active {
  transform: translate(-50%, -50%) scale(0.96);
}

.voice-stop-button {
  background: #e11d48;
  color: #ffffff;
  box-shadow:
    0 22px 46px rgba(225, 29, 72, 0.25),
    inset 0 -0.8rem 0 rgba(159, 18, 57, 0.24);
}

.voice-house {
  position: absolute;
  right: 1.4rem;
  bottom: 1.3rem;
  display: grid;
  width: 6rem;
  height: 6rem;
  place-items: center;
  color: #0369a1;
}

.voice-house span {
  position: absolute;
  bottom: 0.65rem;
  font-size: 1.4rem;
  font-weight: 900;
}

.voice-wave {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(15rem, 58vw);
  height: min(15rem, 58vw);
  border: 0.3rem solid rgba(225, 29, 72, 0.28);
  border-radius: 999px;
  transform: translate(-50%, -50%);
  animation: voice-wave-pulse 1.45s ease-out infinite;
}

.voice-wave-b {
  animation-delay: 0.42s;
}

.voice-record-dot {
  animation: voice-dot-blink 880ms ease-in-out infinite;
}

@keyframes voice-wave-pulse {
  0% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(0.9);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.45);
  }
}

@keyframes voice-dot-blink {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}
</style>
